import { Card, EmptyState, Notice, Pill, ProblemNotice } from '../ui'
import type { BaselineStatus, Condition } from '../../lib/api'
import { isNotFound } from '../../lib/problem'
import type { ArmLanding } from '../../lib/useRetryLanding'
import { CONDITION_WORDS } from '../../lib/recordings'

/**
 * What one condition's baseline amounts to.
 *
 * ── THREE FACTS, AND NOTHING DERIVED FROM THEM ──────────────────────────────
 *
 * `GET /users/{id}/tracking/baseline` returns `n_sessions`, `n_distinct_days`
 * and `low_confidence_flag`. It deliberately does **not** return a numeric
 * confidence or a target day count, and §5.1 says so in as many words. So this
 * shows three facts and computes nothing: no percentage, no "2 of 3 days", no
 * progress bar with a denominator the client picked. The *only* place a
 * countdown legitimately appears is `baseline_status.display` inside a
 * persisted Daily Status, which the package builds and which is rendered
 * verbatim on Today.
 *
 * ── THE DATA CONTRACT ───────────────────────────────────────────────────────
 *
 * One `useResource` per condition over `getBaselineStatus(userId, condition,
 * { signal })` (`api.ts:442-450`), exactly as `Baseline.tsx` does it. The four
 * `Resource` fields go straight through: `data` → `status`, `isLoading`,
 * `error`, `reload` → `onRetry`. `landing` comes from `useRetryLanding` and the
 * CALLER owns the `div.retry-landing` wrapper it lands on.
 *
 * **Two conditions are two hooks and two requests.** This component never
 * receives both and never joins them: EC and EO are separate baselines built
 * from separate histories (§5.1), and one instance speaks for exactly one of
 * them. A caller that wants both — Home — mounts it twice.
 *
 * `variant="compact"` renders NO card, no head, no note and no list wrapper.
 * The caller supplies those; the rows draw their own divider. Both variants run
 * the same branches in the same order.
 *
 * ── "NONE YET" IS TWO ANSWERS, NOT ONE ──────────────────────────────────────
 *
 * A baseline that does not exist arrives as **200** with
 * `{ n_sessions: 0, n_distinct_days: 0, low_confidence_flag: true }`
 * (`brainspot-backend/brainspot/recording/service.py:1264-1273`), and only
 * sometimes as a 404. So the flag is `true` for a baseline nobody has started,
 * and the pill sits BEHIND the zero-sessions guard in both variants: a row that
 * reached the pill first would print "Held low" about a history that does not
 * exist.
 */
export interface BaselineFactsProps {
  condition: Condition
  /** `Resource<BaselineStatus | null>.data`. */
  status: BaselineStatus | null
  /** `.isLoading`. */
  isLoading: boolean
  /** `.error` — a 404 is "none yet", never a failure. */
  error: unknown
  /** `.reload`. */
  onRetry: () => void
  /** From `useRetryLanding`; the caller owns the `div.retry-landing` wrapper. */
  landing?: ArmLanding
  /** `compact` is Home's one-row reading. Default: the full card. */
  variant?: 'default' | 'compact'
}

export function BaselineFacts({
  condition,
  status,
  isLoading,
  error,
  onRetry,
  landing,
  variant = 'default',
}: BaselineFactsProps) {
  const compact = variant === 'compact'

  if (isLoading) {
    // History's per-row shape and word -- the `pending` / `loading` arm of its
    // `ConfidenceCell` (`History.tsx:432-434`); the className is
    // the literal `loading-row` in both variants, which is what lets one
    // `liveRegions` row cover them.
    return compact ? (
      <div className="baseline-row">
        <span className="baseline-row__condition">{CONDITION_WORDS[condition]}</span>
        <span className="baseline-row__wait">
          <span className="loading-row" role="status">
            <span className="spinner" aria-hidden="true" /> Loading
          </span>
        </span>
      </div>
    ) : (
      <Card>
        <p className="loading-row" role="status">
          <span className="spinner" aria-hidden="true" /> Loading your baseline…
        </p>
      </Card>
    )
  }

  // A 404 here is "no history for this condition", which is an explanatory state
  // and not an error (§5.1). Anything else is a failure to report.
  if (error != null) {
    if (isNotFound(error)) {
      return compact ? <NoneYet condition={condition} /> : <NoHistory condition={condition} />
    }
    return (
      <ProblemNotice error={error} onRetry={onRetry} landing={landing}>
        {/* Condition-specific in the compact variant: Home mounts two of these,
            and two failures have to be two findable notices. */}
        {compact
          ? `Your ${CONDITION_WORDS[condition].toLowerCase()} baseline could not be loaded.`
          : 'Your baseline could not be loaded.'}
      </ProblemNotice>
    )
  }

  if (status === null) return null

  if (status.n_sessions === 0) {
    return compact ? <NoneYet condition={condition} /> : <NoHistory condition={condition} />
  }

  // Two fixed renderings of a boolean the payload sent, with the words beside
  // the colour. It would be a decision only if it were keyed on a number.
  const pill = status.low_confidence_flag === true ? (
    <Pill tone="amber" dot>
      Building
    </Pill>
  ) : status.low_confidence_flag === false ? (
    <Pill tone="green" dot>
      Confirmed
    </Pill>
  ) : (
    <Pill>Unavailable</Pill>
  )

  if (compact) {
    const counts
      = `${status.n_sessions} ${status.n_sessions === 1 ? 'recording' : 'recordings'}`
      + ` · ${status.n_distinct_days} ${status.n_distinct_days === 1 ? 'separate day' : 'separate days'}`
    return (
      <div className="baseline-row">
        <span className="baseline-row__condition">{CONDITION_WORDS[condition]}</span>
        <span className="baseline-row__counts">{counts}</span>
        <span className="baseline-row__state">{pill}</span>
      </div>
    )
  }

  return (
    <Card
      eyebrow={`Your baseline · ${CONDITION_WORDS[condition]}`}
      head={
        // A real `<dl>` rather than a bare pill, and `head` rather than `meta`:
        // `e2e/5-product.live.spec.ts:293-325` reads all three facts as
        // `dt:text-is(…) + dd`, the confidence included, and `meta` would wrap
        // this in a `<span class="card__meta">` -- a `<dl>` inside phrasing
        // content, wearing the mono counter-line voice. The `dt` is visually
        // hidden because the eyebrow beside it already names the card.
        <dl className="baseline__confidence">
          <dt className="visually-hidden">Confidence</dt>
          <dd>{pill}</dd>
        </dl>
      }
    >
      <div className="baseline__body">
        {/* REAL `<dl>`s with the `dt` BEFORE the `dd`, not the canvas's two
            `div`s: the label is a label whatever the CSS does with it, and the
            live suite reads the pair. `column-reverse` puts the number above
            the word without the markup lying about which is which.

            The canvas's third tile -- the condition, as a word, in the
            `metric__value` face -- is not built: the head names the condition,
            and a word rendered as a statistic is a statistic that is not one
            (spec §7.1). */}
        <dl className="baseline__stats">
          <div className="metric">
            <dt className="metric__label">Recordings</dt>
            <dd className="metric__value">{status.n_sessions}</dd>
          </div>
          <div className="metric">
            <dt className="metric__label">Separate days</dt>
            <dd className="metric__value">{status.n_distinct_days}</dd>
          </div>
        </dl>
        {/* Sprint 1.0: a second recording on the same day does not advance the
            distinct-day count. The two numbers above can legitimately disagree,
            and this is the sentence that stops that looking like a fault. */}
        <p className="baseline__note">
          Recording twice in one day counts as one baseline day. That is why these two
          numbers can differ.
        </p>
        {status.low_confidence_flag && (
          <Notice tone="warn">
            Your baseline is still short, so comparisons against it are held at low
            confidence. This is a statement about how much history exists, not about the
            quality of any one recording.
          </Notice>
        )}
      </div>
    </Card>
  )
}

/** The compact twin of `NoHistory`: a row, not a card, and never a zero. */
function NoneYet({ condition }: { condition: Condition }) {
  return (
    <div className="baseline-row">
      <span className="baseline-row__condition">{CONDITION_WORDS[condition]}</span>
      <span className="baseline-row__none">None yet</span>
    </div>
  )
}

function NoHistory({ condition }: { condition: Condition }) {
  return (
    <Card>
      <EmptyState title={`No ${CONDITION_WORDS[condition].toLowerCase()} recordings yet`}>
        <p className="product-empty__body">
          A baseline is built from your own scored recordings for one condition. This one
          has none yet, so there is nothing to compare against — which is a starting
          point, not a problem.
        </p>
      </EmptyState>
    </Card>
  )
}
